import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaQuestionCircle, FaCheckCircle, FaClock, FaChartBar,
  FaClipboardList, FaArrowRight, FaExclamationTriangle,
  FaCheck, FaTimes, FaSpinner, FaSync, FaCloud
} from "react-icons/fa";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import DisclosureTopicTooltip from '../../components/DisclosureTopicTooltip';

const HomeForUser = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userStats, setUserStats] = useState({
    totalAssigned: 0,
    totalAnswered: 0,
    inProgress: 0,
    completed: 0,
    completionRate: 0
  });
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [userName, setUserName] = useState('');
  const [userDimensions, setUserDimensions] = useState([]);

  useEffect(() => {
    // Get user name from localStorage
    const name = localStorage.getItem('name') || 'User';
    setUserName(name);
    
    // Fetch user's questions and statistics
    fetchUserData();
    // Fetch user dimensions
    fetchUserDimensions();
  }, []);

  // Fetch current user's assigned dimensions
  const fetchUserDimensions = async () => {
    try {
      const response = await axios.get('/api/user/dimensions');
      if (response.data.success) {
        setUserDimensions(response.data.dimensions || []);
      }
    } catch (error) {
      setUserDimensions([]);
    }
  };

  // Check if user has 'Environment' dimension
  const hasEnvironmentDimension = () => {
    return userDimensions.some(dim => 
      dim && dim.toLowerCase().includes('environment')
    );
  };

  const fetchUserData = async (showToast = false) => {
    if (showToast) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      // Use lightweight summary endpoint for faster loading
      const response = await axios.get('/questions-summary');
      
      if (response.data.status) {
        // Set statistics from API
        setUserStats({
          totalAssigned: response.data.stats.totalAssigned || 0,
          totalAnswered: response.data.stats.totalAnswered || 0,
          inProgress: response.data.stats.inProgress || 0,
          completed: response.data.stats.completed || 0,
          completionRate: response.data.stats.completionRate || 0
        });

        // Set recent questions from API
        setRecentQuestions(response.data.recentQuestions || []);
        
        if (showToast) {
          toastr.success(t('toasts.dataRefreshed'));
        }
      } else {
        // No questions assigned yet
        setUserStats({
          totalAssigned: 0,
          totalAnswered: 0,
          inProgress: 0,
          completed: 0,
          completionRate: 0
        });
        setRecentQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      const errorMessage = error.response?.data?.message || t('toasts.failedToLoad');
      toastr.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchUserData(true);
  };

  const getAnswerTypeLabel = (answerType) => {
    switch (answerType) {
      case 'true':
        return 'Yes';
      case 'false':
        return 'No';
      case 'progress':
        return 'In Progress';
      default:
        return 'Not Started';
    }
  };

  const getAnswerTypeColor = (answerType) => {
    switch (answerType) {
      case 'true':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'false':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const handleStartAnswering = () => {
    navigate('/questions-answers');
  };

  const handleViewAllQuestions = () => {
    navigate('/questions-answers');
  };

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-200 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-2 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 shadow-lg">
        <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {t('dashboard.welcome')}, {userName}!
              </h1>
              <p className="text-green-100 text-sm sm:text-base">
                {t('dashboard.overview')}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="bg-white/20 text-white px-3 py-2 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh data"
              >
                <FaSync className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? t('common.loading') : t('common.refresh')}
              </button>
              <button
                onClick={handleStartAnswering}
                className="bg-white text-green-600 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors flex items-center gap-2 shadow-md"
              >
                <FaQuestionCircle className="w-4 h-4" />
                Start Answering Questions
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              {/* Total Assigned Questions */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.totalAssigned')}</p>
                    <p className="text-2xl font-bold text-blue-600">{userStats.totalAssigned}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('questions.questions')}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaQuestionCircle className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              {/* Answered Questions */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.answered')}</p>
                    <p className="text-2xl font-bold text-green-600">{userStats.totalAnswered}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('dashboard.completed')}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              {/* In Progress */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.inProgress')}</p>
                    <p className="text-2xl font-bold text-yellow-600">{userStats.inProgress}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('dashboard.pending')}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <FaClock className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </div>

              {/* Completion Rate */}
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('dashboard.completionRate')}</p>
                    <p className="text-2xl font-bold text-purple-600">{userStats.completionRate}%</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all"
                        style={{ width: `${userStats.completionRate}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaChartBar className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6 border border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaClipboardList className="w-5 h-5 mr-2 text-green-600" />
            {t('dashboard.quickActions')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={handleStartAnswering}
              className="p-4 rounded-lg border-2 border-green-300 bg-green-50 hover:bg-green-100 transition-all hover:shadow-md text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <FaQuestionCircle className="w-6 h-6 text-green-600" />
                <FaArrowRight className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{t('questions.answerQuestions')}</h3>
              <p className="text-sm text-gray-600">{t('questions.startAnswering')}</p>
            </button>

            {hasEnvironmentDimension() && (
              <button
                onClick={() => navigate('/emissions-questions')}
                className="p-4 rounded-lg border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 transition-all hover:shadow-md text-left"
              >
                <div className="flex items-center justify-between mb-2">
                  <FaCloud className="w-6 h-6 text-blue-600" />
                  <FaArrowRight className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">Emissions Questions</h3>
                <p className="text-sm text-gray-600">Answer emissions-related questions</p>
              </button>
            )}

            <button
              onClick={() => navigate('/profile')}
              className="p-4 rounded-lg border-2 border-purple-300 bg-purple-50 hover:bg-purple-100 transition-all hover:shadow-md text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <FaCheckCircle className="w-6 h-6 text-purple-600" />
                <FaArrowRight className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{t('profile.accountSettings')}</h3>
              <p className="text-sm text-gray-600">{t('profile.manageSettings')}</p>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
              <FaClock className="w-5 h-5 mr-2 text-green-600" />
              {t('dashboard.recentActivity')}
            </h2>
            <div className="flex items-center gap-3">
              {userStats.totalAnswered > 0 && (
                <button
                  onClick={handleViewAllQuestions}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1 bg-transparent hover:bg-green-50 px-2 py-1 rounded transition-colors"
                >
                  View All
                  <FaArrowRight className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="text-sm text-gray-600 hover:text-gray-700 font-medium flex items-center gap-1 bg-transparent hover:bg-gray-50 px-2 py-1 rounded transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <FaSync className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
              <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
              <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
            </div>
          ) : recentQuestions.length > 0 ? (
            <div className="space-y-3">
              {recentQuestions.map((question) => (
                <div
                  key={question.id}
                  onClick={() => {
                    // Navigate to questions-answers with question ID to scroll to specific question
                    const questionId = question.id;
                    navigate(`/questions-answers?questionId=${questionId}`);
                  }}
                  className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer hover:border-green-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getAnswerTypeColor(question.answerType)}`}>
                          {getAnswerTypeLabel(question.answerType)}
                        </span>
                        {question.questionIdentifier && (
                          <span className="text-xs text-gray-500">#{question.questionIdentifier}</span>
                        )}
                      </div>
                      <h3 className="font-medium text-gray-800 mb-1 text-sm sm:text-base line-clamp-2">
                        {question.engagementQuestion}
                      </h3>
                      {question.disclosureTopic && (
                        <p className="text-xs text-gray-500">
                          Topic:{' '}
                          <DisclosureTopicTooltip 
                            disclosureTopic={question.disclosureTopic}
                            disclosureDescription={question.disclosureDescription}
                            className="text-gray-500"
                          />
                        </p>
                      )}
                      {question.updatedAt && (
                        <p className="text-xs text-gray-400 mt-1">
                          Updated: {new Date(question.updatedAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                    <FaArrowRight className="w-4 h-4 text-gray-400 mt-1 sm:mt-0" />
                  </div>
                </div>
              ))}
            </div>
          ) : userStats.totalAssigned === 0 ? (
            <div className="text-center py-8">
              <FaQuestionCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">No questions assigned yet</p>
              <p className="text-sm text-gray-500">Your Company Admin will assign questions to you soon.</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <FaClock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">No recent activity</p>
              <p className="text-sm text-gray-500">Start answering questions to see your activity here.</p>
              <button
                onClick={handleStartAnswering}
                className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Start Answering
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeForUser;

